import type { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_NAME, ROW_ID_DETAIL_DATASET } from "@/constants/dataset";
import type { CountryCode } from "@/types/common";
import {
  DataSchemaType,
  DatasetData,
  DatasetDataType,
  DatasetMetadata,
  DatasetSchemaLabel,
  GetDatasetListResponse,
  OperationStatus,
  OperationStatusType,
  OperationType
} from "@/types/dataset";
import type { DataSettings } from "@/types/dataSetting";

export const BRING_YOUR_OWN_DATA_DATASET_ID = "onboarding-thematic-data-2025";
export const BRING_YOUR_OWN_DATA_ROW_ID = "thematic-data-row-1";
// tourId of the Dataset Management sidebar nav item, spotlighted by the tour's
// opening step so users learn where User Data Management lives.
export const BRING_YOUR_OWN_DATA_DATASET_MANAGEMENT_TOUR_ID =
  "bring-your-own-data-dataset-management";
// tourId of the upload-dropzone step. The dataset page gates the upload dialog's
// visibility on this being the active step, so leaving the step closes it.
export const BRING_YOUR_OWN_DATA_UPLOAD_DROPZONE_TOUR_ID =
  "bring-your-own-data-upload-dropzone";
export const BRING_YOUR_OWN_DATA_IMPORT_SUCCESS_TOAST_ID =
  "bring-your-own-data-import-success-toast";
export const BRING_YOUR_OWN_DATA_IMPORT_SUCCESS_TOUR_ID =
  "bring-your-own-data-import-success-toast";
// tourId of the Data Settings button step on the dataset detail page.
export const BRING_YOUR_OWN_DATA_DATA_SETTINGS_TOUR_ID =
  "bring-your-own-data-data-settings";
// tourIds of the Data Settings popup targets. The detail page gates the
// popup's visibility on one of these targets being active, so leaving them
// (Prev/Next/Skip) closes it — mirroring the upload-dropzone dialog gating.
export const BRING_YOUR_OWN_DATA_DATA_SETTINGS_POPUP_TOUR_ID =
  "bring-your-own-data-data-settings-popup";
export const BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_TOUR_ID =
  "bring-your-own-data-spatial-aggregation";
export const BRING_YOUR_OWN_DATA_SPATIAL_AGGREGATION_POINT_TOUR_ID =
  "bring-your-own-data-spatial-aggregation-point";
// tourId of the Publish button in the confirmation dialog shown at the end of
// the Bring Your Own Data walkthrough.
export const BRING_YOUR_OWN_DATA_PUBLISH_DATA_TOUR_ID =
  "bring-your-own-data-publish-data";
// tourId of the completed configuration badges on the dataset detail page.
export const BRING_YOUR_OWN_DATA_DATASET_CONFIGURATION_TOUR_ID =
  "bring-your-own-data-dataset-configuration";

const timestamp = "2025-01-15T09:00:00.000Z";

const schema: DatasetMetadata["schema"] = [
  {
    id: "business-id-schema",
    code: "business_id",
    attribute_name: "Business ID",
    data_type: DataSchemaType.ID,
    data_label: DatasetSchemaLabel.ID,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: true,
    is_spatial: false,
    position: 1,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "name-schema",
    code: "name",
    attribute_name: "Name",
    data_type: DataSchemaType.TEXT,
    data_label: DatasetSchemaLabel.TITLE,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 2,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "category-schema",
    code: "category",
    attribute_name: "Category",
    data_type: DataSchemaType.TEXT,
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 3,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "business-format-schema",
    code: "business_format",
    attribute_name: "Business Format",
    data_type: DataSchemaType.TEXT,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 4,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "service-style-schema",
    code: "service_style",
    attribute_name: "Service Style",
    data_type: DataSchemaType.TEXT,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 5,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "address-schema",
    code: "address",
    attribute_name: "Address",
    data_type: DataSchemaType.TEXT,
    data_label: DatasetSchemaLabel.ADDRESS,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 6,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "city-schema",
    code: "city",
    attribute_name: "City",
    data_type: DataSchemaType.TEXT,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 7,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "latitude-schema",
    code: "latitude",
    attribute_name: "Latitude",
    data_type: DataSchemaType.LATITUDE,
    data_label: DatasetSchemaLabel.LATITUDE,
    is_nullable: false,
    is_reference_geo: true,
    is_reference_id: false,
    is_spatial: true,
    position: 8,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  },
  {
    id: "longitude-schema",
    code: "longitude",
    attribute_name: "Longitude",
    data_type: DataSchemaType.LONGITUDE,
    data_label: DatasetSchemaLabel.LONGITUDE,
    is_nullable: false,
    is_reference_geo: true,
    is_reference_id: false,
    is_spatial: true,
    position: 9,
    created_at: timestamp,
    updated_at: timestamp,
    is_visible: true,
    paired_key_attribute_id: null
  }
];

const datasetDescription =
  "Contains data on restaurants, cafes, bars, food courts, and takeaway spots, categorized by type and business format. Includes location details and service styles.";

const datasetMetadata: DatasetMetadata = {
  id: BRING_YOUR_OWN_DATA_DATASET_ID,
  name: "Thematic Data 2025",
  country_id: "id",
  source: "user",
  spatial_aggregation_type: "poi",
  short_description: datasetDescription,
  published: false,
  created_at: timestamp,
  updated_at: timestamp,
  total_rows: 1,
  size_kb: 128,
  dataset_size_kb: 128,
  schema
};

const datasetData: DatasetData = {
  dataset_id: BRING_YOUR_OWN_DATA_DATASET_ID,
  name: "Thematic Data 2025",
  spatial_aggregation_type: "poi",
  dataset_size_kb: 128,
  columns: [
    {
      code: ROW_ID_DETAIL_DATASET,
      name: ROW_ID_DETAIL_DATASET,
      data_type: DatasetDataType.TYPE_ID,
      data_label: DatasetSchemaLabel.ID,
      is_visible: false,
      is_reference_geo: false
    },
    {
      schema_id: "business-id-schema",
      code: "business_id",
      name: "Business ID",
      data_type: DatasetDataType.TYPE_ID,
      data_label: DatasetSchemaLabel.ID,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "name-schema",
      code: "name",
      name: "Name",
      data_type: DatasetDataType.TYPE_STRING,
      data_label: DatasetSchemaLabel.TITLE,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "category-schema",
      code: "category",
      name: "Category",
      data_type: DatasetDataType.TYPE_STRING,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "business-format-schema",
      code: "business_format",
      name: "Business Format",
      data_type: DatasetDataType.TYPE_STRING,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "service-style-schema",
      code: "service_style",
      name: "Service Style",
      data_type: DatasetDataType.TYPE_STRING,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "address-schema",
      code: "address",
      name: "Address",
      data_type: DatasetDataType.TYPE_STRING,
      data_label: DatasetSchemaLabel.ADDRESS,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "city-schema",
      code: "city",
      name: "City",
      data_type: DatasetDataType.TYPE_STRING,
      is_visible: true,
      is_reference_geo: false
    },
    {
      schema_id: "latitude-schema",
      code: "latitude",
      name: "Latitude",
      data_type: DatasetDataType.TYPE_LATITUDE,
      data_label: DatasetSchemaLabel.LATITUDE,
      is_visible: true,
      is_reference_geo: true
    },
    {
      schema_id: "longitude-schema",
      code: "longitude",
      name: "Longitude",
      data_type: DatasetDataType.TYPE_LONGITUDE,
      data_label: DatasetSchemaLabel.LONGITUDE,
      is_visible: true,
      is_reference_geo: true
    }
  ],
  rows: [
    [
      BRING_YOUR_OWN_DATA_ROW_ID,
      "FNB-001",
      "Kopi Senja",
      "Cafe",
      "Independent",
      "Dine-in and takeaway",
      "Jl. Melati No. 10",
      "Jakarta",
      -6.2088,
      106.8456
    ]
  ]
};

const emptyDatasetList: GetDatasetListResponse = {
  data: [],
  meta: {
    pagination: {
      next: 1,
      prev: 1,
      per_page: 10,
      total: 0,
      total_pages: 0
    }
  }
};

// Data Settings shown on the spatial-aggregation tour step: country pre-selected
// to match the mock dataset's country_id, spatial aggregation left unselected so
// the step can spotlight the open options list.
const dataSettingWithCountry: DataSettings = {
  country: {
    label: COUNTRY_NAME[datasetMetadata.country_id as CountryCode],
    value: datasetMetadata.country_id
  }
};

const pointGeometryAttributeOptions: IOptionItem[] = [
  {
    label: "Location Point Wkt",
    value: "location-point-wkt-schema",
    description: "location_point_wkt"
  },
  {
    label: "Store Coordinate",
    value: "store-coordinate-schema",
    description: "store_coordinate"
  }
];

const dataSettingWithPoint: DataSettings = {
  ...dataSettingWithCountry,
  spatialAggregation: {
    type: {
      label: "Point",
      value: "poi"
    },
    pointAttribute: pointGeometryAttributeOptions[0].value
  }
};

const importNewOperation: OperationStatus = {
  id: "bring-your-own-data-import-new-operation",
  dataset_id: BRING_YOUR_OWN_DATA_DATASET_ID,
  operation_type: OperationType.IMPORT_NEW,
  status: OperationStatusType.SUCCESS,
  import_count: {
    duplicate: 0,
    failed: 0,
    success: 1,
    total: 1
  }
};

export const BRING_YOUR_OWN_DATA_MOCKS = {
  emptyDatasetList,
  datasetMetadata,
  datasetData,
  dataSettingWithCountry,
  dataSettingWithPoint,
  pointGeometryAttributeOptions,
  importNewOperation,
  highlightedRow: {
    rowId: BRING_YOUR_OWN_DATA_ROW_ID,
    mode: "add",
    isHighlight: true
  }
} as const;
